import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SiaLayout } from '../interfaces/sia-layout';
import { SiaMenu } from '../interfaces/sia-menu';
import { SiaIndexedDbService } from './indexed-db.service';

@Injectable({
  providedIn: 'root',
})
export class SiaCacheDatabaseService extends SiaIndexedDbService {
  private stores: any = {
    STORE_ACTIONS: 'actions',
    STORE_COLLECTIONS: 'collections',
    STORE_CONFIG: 'config',
    STORE_LAYOUTS: 'layouts',
    STORE_MENU_LINKS: 'menu-links',
    STORE_USER_MENU: 'user-menu',
    STORE_RESPONSE: 'response',
  };
  private _storeClearedSubject: Subject<string> = new Subject();

  public get onStoreCleared$(): Subject<string> {
    return this._storeClearedSubject;
  }

  async clearDatabase(): Promise<boolean> {
    const results = await Promise.all([
      this.clearAllActions(),
      this.clearAllCollections(),
      this.clearAllConfigs(),
      this.clearAllLayouts(),
      this.clearAllMenuLinks(),
      this.clearAllUserMenu(),
      this.clearAllResponse(),
    ]);
    return results.every(Boolean);
  }

  //#region ACTIONS
  private getActionsKey(componentId: string, mode?: string) {
    return (componentId + '--' + mode).toLowerCase();
  }
  getActions(componentId: string, mode?: string) {
    const key = this.getActionsKey(componentId, mode);
    return this.getCacheDataValue(this.stores.STORE_ACTIONS, key);
  }
  setActions(componentId: string, actions: any, mode?: string) {
    const key = this.getActionsKey(componentId, mode);
    return this.setCacheDataValue(this.stores.STORE_ACTIONS, key, actions);
  }
  clearActions(componentId: string, mode: string) {
    const key = this.getActionsKey(componentId, mode);
    return this.clearRecordsContainingKeys(this.stores.STORE_ACTIONS, key);
  }
  clearAllActions() {
    return this.clearStore(this.stores.STORE_ACTIONS);
  }
  //#endregion

  //#region COLLECTIONS
  getCollection(key: string) {
    return this.getCacheDataValue(this.stores.STORE_COLLECTIONS, key.toLowerCase());
  }
  setCollection(key: string, items: any[]) {
    return this.setCacheDataValue(this.stores.STORE_COLLECTIONS, key.toLowerCase(), items);
  }
  clearAllCollections() {
    return this.clearStore(this.stores.STORE_COLLECTIONS);
  }
  clearCollection(collectionId: string) {
    return this.deleteByKey(this.stores.STORE_COLLECTIONS, collectionId.toLowerCase());
  }
  //#endregion

  //#region CONFIG
  private getComponentActionsConfigurationKey(componentId: string) {
    return ('actions-' + componentId).toLowerCase();
  }
  getComponentActionsConfiguration(componentId: string) {
    const key = this.getComponentActionsConfigurationKey(componentId);
    return this.getCacheDataValue(this.stores.STORE_CONFIG, key);
  }
  setComponentActionsConfiguration(componentId: string, configuration: any) {
    const key = this.getComponentActionsConfigurationKey(componentId);
    return this.setCacheDataValue(this.stores.STORE_CONFIG, key, configuration);
  }
  clearComponentActionsConfiguration(componentId: string) {
    const key = this.getComponentActionsConfigurationKey(componentId);
    return this.clearRecordsContainingKeys(this.stores.STORE_CONFIG, key);
  }

  clearAllConfigs() {
    return this.clearStore(this.stores.STORE_CONFIG);
  }
  //#endregion

  //#region LAYOUTS
  async layoutsLoaded(): Promise<boolean> {
    const count = await this.getCount(this.stores.STORE_LAYOUTS);
    return !!count && count > 0;
  }
  getLayouts() {
    return this.getAll<SiaLayout>(this.stores.STORE_LAYOUTS);
  }
  clearAllLayouts() {
    return this.clearStore(this.stores.STORE_LAYOUTS);
  }
  async setLayouts(layouts: SiaLayout[]) {
    await this.clearStore(this.stores.STORE_LAYOUTS);
    return this.bulkAdd(
      this.stores.STORE_LAYOUTS,
      layouts.map((l) => ({ ...l, id: l.id?.toLowerCase() })),
    );
  }
  getLayout(id: string): Promise<SiaLayout> {
    return this.getById(this.stores.STORE_LAYOUTS, id?.toLowerCase());
  }
  setLayout(layout: SiaLayout) {
    return this.add(this.stores.STORE_LAYOUTS, { ...layout, id: layout.id?.toLowerCase() });
  }
  updateLayout(layout: SiaLayout) {
    return this.update(this.stores.STORE_LAYOUTS, { ...layout, id: layout.id?.toLowerCase() });
  }
  //#endregion

  //#region MENU_LINKS
  getMenuLinks(key: string, deviceType: string) {
    return this.getCacheDataValue(this.stores.STORE_MENU_LINKS, (key + deviceType).toLowerCase());
  }
  setMenuLinks(key: string, items: any[]) {
    return this.setCacheDataValue(this.stores.STORE_MENU_LINKS, key.toLowerCase(), items);
  }
  clearAllMenuLinks() {
    return this.clearStore(this.stores.STORE_MENU_LINKS);
  }
  //#endregion

  //#region USER_MENU
  async menuLoaded(): Promise<boolean> {
    const count = await this.getCount(this.stores.STORE_USER_MENU);
    return !!count && count > 0;
  }
  getUserMenus() {
    return this.getAll<SiaMenu>(this.stores.STORE_USER_MENU);
  }
  getUserMenu(menuId: string): Promise<SiaMenu> {
    return this.getById(this.stores.STORE_USER_MENU, menuId);
  }
  async setUserMenus(menus: SiaMenu[]) {
    await this.clearStore(this.stores.STORE_USER_MENU);
    await this.bulkAdd(this.stores.STORE_USER_MENU, menus);
    await this.clearCollection('user-menu');
  }
  async setMenu(menu: SiaMenu) {
    await this.add(this.stores.STORE_USER_MENU, menu);
    await this.clearCollection('user-menu');
  }
  async updateUserMenu(menu: SiaMenu) {
    await this.update(this.stores.STORE_USER_MENU, menu);
    await this.clearCollection('user-menu');
  }
  async clearAllUserMenu() {
    await this.clearStore(this.stores.STORE_USER_MENU);
    await this.clearCollection('user-menu');
  }
  async clearUserMenu(menuId: string) {
    await this.deleteByKey(this.stores.STORE_USER_MENU, menuId);
    await this.clearCollection('user-menu');
  }
  //#endregion

  //#region RESPONSE
  setResponse(key: string, data: any) {
    return this.setCacheDataValue(this.stores.STORE_RESPONSE, key.toLowerCase(), data);
  }
  getResponse(key: string) {
    return this.getCacheDataValue(this.stores.STORE_RESPONSE, key.toLowerCase());
  }
  clearAllResponse() {
    return this.clearStore(this.stores.STORE_RESPONSE);
  }
  async clearAllStoresContaining(concatenatedKeys: string | undefined) {
    const promises: Promise<boolean>[] = [];
    for (const key in this.stores) {
      if (key === 'STORE_RESPONSE' || key === 'STORE_COLLECTIONS') {
        promises.push(this.clearRecordsContainingKeys(this.stores[key], concatenatedKeys?.toLowerCase()));
      }
    }
    await Promise.all(promises);
    this._storeClearedSubject.next(concatenatedKeys ?? '');
  }

  clearAllResponsesContaining(concatenatedKeys: string | undefined) {
    return this.clearRecordsContainingKeys(this.stores.STORE_RESPONSE, concatenatedKeys?.toLowerCase());
  }
  clearResponse(key: string) {
    return this.deleteByKey(this.stores.STORE_RESPONSE, key.toLowerCase());
  }
  createResponseKey(preKey: string, url: string, options: any) {
    let key = preKey ? preKey + '-' + url : url;
    if (options) key = key + '-' + JSON.stringify(options);
    return key.toLowerCase();
  }
  //#endregion
}
